/** Shared marketing copy and plan helpers for Landing + public SEO pages. */

export const FALLBACK_PLANS = [
  {
    name: { en: 'Free', es: 'Gratis' },
    price: 0,
    highlight: false,
    features: {
      en: ['1 WhatsApp profile', '100 AI tokens/mo', '100 contacts', 'Basic AI responses'],
      es: ['1 perfil WhatsApp', '100 tokens IA/mes', '100 contactos', 'Respuestas IA básicas'],
    },
  },
  {
    name: { en: 'Starter', es: 'Inicial' },
    price: 9.99,
    highlight: true,
    features: {
      en: ['1 WhatsApp profile', '2,000 AI tokens/mo', '1,000 contacts', '1,000 campaign msgs', '1 team member', '2GB storage'],
      es: ['1 perfil WhatsApp', '2,000 tokens IA/mes', '1,000 contactos', '1,000 msgs campaña', '1 miembro de equipo', '2GB almacenamiento'],
    },
  },
  {
    name: { en: 'Professional', es: 'Profesional' },
    price: 29.99,
    highlight: false,
    features: {
      en: ['3 WhatsApp profiles', '25,000 AI tokens/mo', '10,000 contacts', '10,000 campaign msgs', '5 team members', '25GB storage'],
      es: ['3 perfiles WhatsApp', '25,000 tokens IA/mes', '10,000 contactos', '10,000 msgs campaña', '5 miembros de equipo', '25GB almacenamiento'],
    },
  },
];

export function landingFeaturesFromPlan(plan, lang) {
  const aiNum = Number(plan.ai_tokens);
  const ai = !Number.isNaN(aiNum) && aiNum > 0 ? aiNum : 0;
  const approx = ai > 0 ? Math.floor(ai / 400) : 0;
  const lines = [];
  lines.push(
    lang === 'en'
      ? `${plan.qr_account ?? 0} WhatsApp profile(s)`
      : `${plan.qr_account ?? 0} perfil(es) WhatsApp`,
  );
  lines.push(
    lang === 'en'
      ? `${ai.toLocaleString()} AI tokens/mo (~${approx} messages)`
      : `${ai.toLocaleString()} tokens IA/mes (~${approx} mensajes)`,
  );
  lines.push(
    lang === 'en'
      ? `${plan.contact_limit ?? '—'} contacts`
      : `${plan.contact_limit ?? '—'} contactos`,
  );
  lines.push(
    plan.allow_chatbot
      ? lang === 'en'
        ? 'Chatbot included'
        : 'Chatbot incluido'
      : lang === 'en'
        ? 'Chatbot not included'
        : 'Chatbot no incluido',
  );
  if (plan.plan_duration_in_days) {
    lines.push(
      lang === 'en'
        ? `${plan.plan_duration_in_days} days per term`
        : `${plan.plan_duration_in_days} días por período`,
    );
  }
  if (plan.short_description) {
    const short = String(plan.short_description).replace(/\s+/g, ' ').trim();
    if (short.length > 120) lines.push(short.slice(0, 117) + '…');
    else lines.push(short);
  }
  if (plan.storage) {
    lines.push(
      lang === 'en' ? `Storage: ${plan.storage}` : `Almacenamiento: ${plan.storage}`,
    );
  }
  return lines;
}

/** Short answers for landing accordion; `aExpanded` for dedicated FAQ page (SEO). */
export const FAQ_DATA = [
  {
    q: { en: 'Do I need a special WhatsApp number?', es: '¿Necesito un número especial de WhatsApp?' },
    a: {
      en: 'No. ReplyTek works with your existing WhatsApp number. Just scan a QR code and you\'re connected.',
      es: 'No. ReplyTek funciona con tu número de WhatsApp actual. Solo escanea un código QR y listo.',
    },
    aExpanded: {
      en: 'No. ReplyTek works with your existing WhatsApp number. Just scan a QR code and you\'re connected. You keep using the same Business or personal line your customers already know—there is no need to migrate numbers or buy a second SIM. The connection is designed to be quick and reversible if you ever want to disconnect.',
      es: 'No. ReplyTek funciona con tu número de WhatsApp actual. Solo escanea un código QR y listo. Sigues usando la misma línea que tus clientes ya conocen—no hace falta migrar números ni comprar otro chip. La conexión está pensada para ser rápida y reversible si algún día quieres desvincularla.',
    },
  },
  {
    q: { en: 'Is my data secure?', es: '¿Mis datos están seguros?' },
    a: {
      en: 'Yes. Your conversations stay private and you maintain full control of your WhatsApp account at all times.',
      es: 'Sí. Tus conversaciones son privadas y mantienes control total de tu cuenta de WhatsApp en todo momento.',
    },
    aExpanded: {
      en: 'Yes. Your conversations stay private and you maintain full control of your WhatsApp account at all times. You decide what business information the assistant can use to reply, and you can review or adjust settings whenever you need. Industry-standard practices help protect data in transit and at rest as you scale customer conversations.',
      es: 'Sí. Tus conversaciones son privadas y mantienes control total de tu cuenta de WhatsApp en todo momento. Tú decides qué información del negocio puede usar el asistente para responder, y puedes revisar o ajustar la configuración cuando quieras. Se aplican buenas prácticas para proteger los datos en tránsito y en reposo a medida que crece tu volumen de conversaciones.',
    },
  },
  {
    q: { en: 'How long does setup take?', es: '¿Cuánto tarda la configuración?' },
    a: {
      en: 'Less than 2 minutes. Scan the QR code, enter your business info, and the AI starts working immediately.',
      es: 'Menos de 2 minutos. Escanea el QR, ingresa tu info de negocio, y la IA empieza a funcionar de inmediato.',
    },
    aExpanded: {
      en: 'Less than 2 minutes. Scan the QR code, enter your business info, and the AI starts working immediately. Most teams connect WhatsApp first, then add hours, services, and FAQs so answers sound like their brand. You can refine wording and tone later without redeploying code or waiting on developers.',
      es: 'Menos de 2 minutos. Escanea el QR, ingresa tu info de negocio, y la IA empieza a funcionar de inmediato. La mayoría conecta WhatsApp primero y luego añade horarios, servicios y preguntas frecuentes para que las respuestas suenen a su marca. Puedes afinar el tono después sin desplegar código ni depender de desarrolladores.',
    },
  },
  {
    q: { en: 'Can I customize what the AI says?', es: '¿Puedo personalizar lo que dice la IA?' },
    a: {
      en: 'Absolutely. You control the AI\'s personality, language, response style, and the product/service information it uses to answer customers.',
      es: 'Totalmente. Controlas la personalidad de la IA, idioma, estilo de respuesta, y la información de productos/servicios que usa para responder.',
    },
    aExpanded: {
      en: 'Absolutely. You control the AI\'s personality, language, response style, and the product/service information it uses to answer customers. Update catalogs, promotions, or policies in one place so replies stay accurate. That keeps messaging consistent across peak hours, weekends, and holidays without extra headcount.',
      es: 'Totalmente. Controlas la personalidad de la IA, idioma, estilo de respuesta, y la información de productos/servicios que usa para responder. Actualiza catálogo, promociones o políticas en un solo lugar para que las respuestas sigan siendo precisas. Así mantienes un mensaje coherente en horas pico, fines de semana y festivos sin sumar más personal.',
    },
  },
  {
    q: { en: 'Do I need technical knowledge?', es: '¿Necesito conocimientos técnicos?' },
    a: {
      en: 'Zero. If you can use WhatsApp, you can use ReplyTek. Everything is configured with clicks and plain language.',
      es: 'Cero. Si puedes usar WhatsApp, puedes usar ReplyTek. Todo se configura con clics y lenguaje natural.',
    },
    aExpanded: {
      en: 'Zero. If you can use WhatsApp, you can use ReplyTek. Everything is configured with clicks and plain language. There are no webhooks to wire, scripts to host, or APIs to learn for day-to-day operation—ideal for owners and ops teams who want automation without an engineering project.',
      es: 'Cero. Si puedes usar WhatsApp, puedes usar ReplyTek. Todo se configura con clics y lenguaje natural. No hay webhooks que cablear, scripts que alojar ni APIs que aprender para el día a día—ideal para dueños y equipos de operaciones que quieren automatizar sin un proyecto de ingeniería.',
    },
  },
];

export const SOLUTION_FEATURE_ITEMS = [
  { icon: '⚡', text: { en: 'Instant AI replies 24/7', es: 'Respuestas IA al instante 24/7' } },
  { icon: '🏪', text: { en: 'Uses your hours, address, phone & website', es: 'Usa horarios, dirección, teléfono y web' } },
  { icon: '🌐', text: { en: 'Replies in your customer\'s language (auto-detect)', es: 'Responde en el idioma del cliente (auto-detect)' } },
  { icon: '🖱️', text: { en: 'No code — set up with clicks', es: 'Sin código — configura todo con clics' } },
];

export const USE_CASE_CARDS = [
  {
    icon: '🛒',
    title: { en: 'E-commerce', es: 'E-commerce' },
    desc: {
      en: 'Answer product questions and share prices using your catalog and business info.',
      es: 'Responde dudas de productos y comparte precios con tu catálogo e info del negocio.',
    },
  },
  {
    icon: '🍔',
    title: { en: 'Restaurants', es: 'Restaurantes' },
    desc: {
      en: 'Answer questions, share menu and hours, and respond during busy times.',
      es: 'Responde preguntas, comparte menú y horarios, y atiende en horas de mayor demanda.',
    },
  },
  {
    icon: '📦',
    title: { en: 'Dropshipping', es: 'Dropshipping' },
    desc: {
      en: 'Reply to product inquiries with consistent, accurate information.',
      es: 'Responde consultas de productos con información clara y consistente.',
    },
  },
  {
    icon: '💼',
    title: { en: 'Services', es: 'Servicios' },
    desc: {
      en: 'Answer FAQs about your services, pricing basics, and how to reach you.',
      es: 'Responde preguntas frecuentes sobre servicios, precios básicos y cómo contactarte.',
    },
  },
];
